import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

export interface CalendarAvailabilitySlot {
  id: string;
  title: string | null;
  start_time: string;
  end_time: string;
  is_booked: boolean;
  booked_by_name: string | null;
  booked_by_email: string | null;
  booked_at: string | null;
  is_recurring: boolean;
  recurrence_pattern: string | null;
  recurrence_days: number[] | null;
  booking_link_token: string | null;
  user_id: string;
  vertriebspartner_id: string | null;
  tenant_id: string | null;
  created_at: string;
  updated_at: string;
  // New visibility matrix fields
  visible_to_customers: boolean;
  visible_to_partners: boolean;
  partner_scope: 'hierarchy' | 'network';
  is_public: boolean;
  max_capacity: number;
  current_bookings: number;
}

export interface CreateAvailabilitySlotInput {
  title?: string;
  start_time: string;
  end_time: string;
  is_recurring?: boolean;
  recurrence_pattern?: string;
  recurrence_days?: number[];
  // New visibility matrix fields
  visible_to_customers?: boolean;
  visible_to_partners?: boolean;
  partner_scope?: 'hierarchy' | 'network';
  is_public?: boolean;
  max_capacity?: number;
}

export interface BookSlotInput {
  slot_id: string;
  booked_by_name: string;
  booked_by_email: string;
  message?: string;
}

export interface SlotBooking {
  id: string;
  slot_id: string;
  booked_by_name: string;
  booked_by_email: string;
  booked_by_user_id: string | null;
  booked_at: string;
  notes: string | null;
  status: string;
}

export const useCalendarAvailabilitySlots = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: slots = [], isLoading } = useQuery({
    queryKey: ['calendar-availability-slots', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calendar_availability_slots')
        .select('*')
        .order('start_time', { ascending: true });

      if (error) {
        console.error('Error loading availability slots:', error);
        throw error;
      }

      // Map with type assertion for new columns not yet in generated types
      return (data || []).map((slot: Record<string, unknown>) => ({
        ...slot,
        visible_to_customers: (slot.visible_to_customers as boolean) ?? false,
        visible_to_partners: (slot.visible_to_partners as boolean) ?? false,
        partner_scope: ((slot.partner_scope as string) ?? 'hierarchy') as 'hierarchy' | 'network',
        is_public: (slot.is_public as boolean) ?? false,
        max_capacity: (slot.max_capacity as number) ?? 1,
        current_bookings: (slot.current_bookings as number) ?? 0,
      })) as CalendarAvailabilitySlot[];
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const createSlot = useMutation({
    mutationFn: async (input: CreateAvailabilitySlotInput) => {
      // Generate a unique booking link token
      const bookingToken = crypto.randomUUID();
      
      const { data, error } = await supabase
        .from('calendar_availability_slots')
        .insert({
          title: input.title,
          start_time: input.start_time,
          end_time: input.end_time,
          is_recurring: input.is_recurring ?? false,
          recurrence_pattern: input.recurrence_pattern,
          recurrence_days: input.recurrence_days,
          user_id: user?.id,
          is_booked: false,
          booking_link_token: bookingToken,
          visible_to_customers: input.visible_to_customers ?? false,
          visible_to_partners: input.visible_to_partners ?? false,
          partner_scope: input.partner_scope ?? 'hierarchy',
          is_public: input.is_public ?? false,
          max_capacity: input.max_capacity ?? 1,
          current_bookings: 0,
        } as Record<string, unknown>)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-availability-slots'] });
      toast({
        title: "Zeitfenster erstellt",
        description: "Das verfügbare Zeitfenster wurde erfolgreich angelegt.",
      });
    },
    onError: (error) => {
      console.error('Error creating availability slot:', error);
      toast({
        title: "Fehler",
        description: "Das Zeitfenster konnte nicht erstellt werden.",
        variant: "destructive",
      });
    },
  });

  const updateSlot = useMutation({
    mutationFn: async ({ id, ...input }: Partial<CreateAvailabilitySlotInput> & { id: string }) => {
      const { data, error } = await supabase
        .from('calendar_availability_slots')
        .update(input as Record<string, unknown>)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-availability-slots'] });
      toast({
        title: "Zeitfenster aktualisiert",
        description: "Das Zeitfenster wurde erfolgreich aktualisiert.",
      });
    },
    onError: (error) => {
      console.error('Error updating availability slot:', error);
      toast({
        title: "Fehler",
        description: "Das Zeitfenster konnte nicht aktualisiert werden.",
        variant: "destructive",
      });
    },
  });

  const deleteSlot = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('calendar_availability_slots')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-availability-slots'] });
      toast({
        title: "Zeitfenster gelöscht",
        description: "Das Zeitfenster wurde erfolgreich gelöscht.",
      });
    },
    onError: (error) => {
      console.error('Error deleting availability slot:', error);
      toast({
        title: "Fehler",
        description: "Das Zeitfenster konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    },
  });

  const bookSlot = useMutation({
    mutationFn: async ({ slot_id, booked_by_name, booked_by_email, message }: BookSlotInput) => {
      // Insert into junction table for proper multi-booking support
      const { data: booking, error: bookingError } = await supabase
        .from('calendar_slot_bookings')
        .insert({
          slot_id,
          booked_by_name,
          booked_by_email,
          booked_by_user_id: user?.id,
          notes: message,
        })
        .select()
        .single();

      if (bookingError) throw bookingError;

      // Update slot booking count via direct increment
      const slot = slots.find(s => s.id === slot_id);
      if (slot) {
        await supabase
          .from('calendar_availability_slots')
          .update({
            current_bookings: (slot.current_bookings || 0) + 1,
            is_booked: ((slot.current_bookings || 0) + 1) >= (slot.max_capacity || 1),
            booked_by_name,
            booked_by_email,
            booked_at: new Date().toISOString(),
          } as Record<string, unknown>)
          .eq('id', slot_id);
      }

      return booking;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-availability-slots'] });
      toast({
        title: "Termin gebucht",
        description: "Der Termin wurde erfolgreich gebucht.",
      });
    },
    onError: (error) => {
      console.error('Error booking slot:', error);
      toast({
        title: "Fehler",
        description: "Der Termin konnte nicht gebucht werden. Möglicherweise wurde er bereits vergeben.",
        variant: "destructive",
      });
    },
  });

  const cancelBooking = useMutation({
    mutationFn: async (slot_id: string) => {
      const { data, error } = await supabase
        .from('calendar_availability_slots')
        .update({
          is_booked: false,
          booked_by_name: null,
          booked_by_email: null,
          booked_at: null,
          current_bookings: 0,
        })
        .eq('id', slot_id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-availability-slots'] });
      toast({
        title: "Buchung storniert",
        description: "Die Buchung wurde erfolgreich storniert.",
      });
    },
    onError: (error) => {
      console.error('Error canceling booking:', error);
      toast({
        title: "Fehler",
        description: "Die Buchung konnte nicht storniert werden.",
        variant: "destructive",
      });
    },
  });

  // Get available (unbooked) slots
  const availableSlots = slots.filter(slot => !slot.is_booked && (slot.current_bookings || 0) < (slot.max_capacity || 1));
  
  // Get booked slots
  const bookedSlots = slots.filter(slot => slot.is_booked || (slot.current_bookings || 0) >= (slot.max_capacity || 1));

  // Generate booking link URL for a slot (only for public slots)
  const getBookingLink = (slotId: string) => {
    const slot = slots.find(s => s.id === slotId);
    if (!slot?.booking_link_token) return null;
    return `${window.location.origin}/book/${slot.booking_link_token}`;
  };

  // Get visibility label for display
  const getVisibilityLabel = (slot: CalendarAvailabilitySlot): string => {
    if (slot.is_public) return 'Öffentlich';
    const labels: string[] = [];
    if (slot.visible_to_customers) labels.push('Kunden');
    if (slot.visible_to_partners) {
      labels.push(slot.partner_scope === 'network' ? 'Netzwerk' : 'Hierarchie');
    }
    return labels.length > 0 ? labels.join(', ') : 'Privat';
  };

  return {
    slots,
    availableSlots,
    bookedSlots,
    isLoading,
    createSlot,
    updateSlot,
    deleteSlot,
    bookSlot,
    cancelBooking,
    getBookingLink,
    getVisibilityLabel,
    currentUserId: user?.id,
  };
};

// Types for the public booking RPC functions
interface PublicSlotInfo {
  id: string;
  title: string;
  start_time: string;
  end_time: string;
  max_capacity: number;
  current_bookings: number;
  user_id: string;
  owner_name: string;
  is_available: boolean;
}

interface BookingResult {
  success: boolean;
  error?: string;
  booking_id?: string;
  slot_title?: string;
  start_time?: string;
  end_time?: string;
}

// Hook for public booking (no authentication required)
export const usePublicSlotBooking = (token: string) => {
  const { toast } = useToast();

  const { data: slotInfo, isLoading, error } = useQuery({
    queryKey: ['public-slot', token],
    queryFn: async () => {
      // Use raw fetch for RPC since types aren't generated yet
      const response = await fetch(
        `https://hwjnhvbrkvzumzhakgla.supabase.co/rest/v1/rpc/get_public_slot_by_token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imh3am5odmJya3Z6dW16aGFrZ2xhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTIzNDc0OTMsImV4cCI6MjA2NzkyMzQ5M30.RX_d4clsbfBKTdydWDncItpZ2okA38vLJg74xfybfdA',
          },
          body: JSON.stringify({ p_token: token }),
        }
      );

      if (!response.ok) {
        throw new Error('Zeitfenster nicht gefunden');
      }

      const data = await response.json();
      if (!data || data.length === 0) {
        throw new Error('Zeitfenster nicht gefunden');
      }
      
      return data[0] as PublicSlotInfo;
    },
    enabled: !!token,
    staleTime: 30 * 1000, // 30 seconds
  });

  const bookPublicSlot = useMutation({
    mutationFn: async ({ name, email, message }: { name: string; email: string; message?: string }) => {
      // Use raw fetch for RPC since types aren't generated yet
      const response = await fetch(
        `https://hwjnhvbrkvzumzhakgla.supabase.co/rest/v1/rpc/book_public_slot`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imh3am5odmJya3Z6dW16aGFrZ2xhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTIzNDc0OTMsImV4cCI6MjA2NzkyMzQ5M30.RX_d4clsbfBKTdydWDncItpZ2okA38vLJg74xfybfdA',
          },
          body: JSON.stringify({
            p_token: token,
            p_booked_by_name: name,
            p_booked_by_email: email,
            p_message: message || null,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Buchung fehlgeschlagen');
      }

      const result = await response.json() as BookingResult;
      if (!result.success) {
        throw new Error(result.error || 'Buchung fehlgeschlagen');
      }
      
      return result;
    },
    onSuccess: () => {
      toast({
        title: "Termin gebucht!",
        description: "Sie erhalten in Kürze eine Bestätigung per E-Mail.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Buchung fehlgeschlagen",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    slotInfo,
    isLoading,
    error,
    bookPublicSlot,
  };
};
